import { FormGroup } from '@angular/forms';

import { VendaTipoBloqueioEnum } from '../../../../../shared/enums/vendas/venda-tipo-bloqueio.enum';
import { VendaLiberacao } from '../../../../../shared/services/models/vendas/venda-liberacao.model';
import { ClienteDocumentoStruct } from '../../../../../shared/services/structs/vendas/cliente-documento.struct';
import { LiberacaoPresencialMotivoStruct } from '../../../../../shared/services/structs/vendas/liberacao-presencial-motivo.struct';
import { descontoEfetivoDoItem } from '../vd-totais/venda-totais.calculo';

/**
 * Um bloqueio que impede o salvamento até um supervisor liberar (§6.5). É o que o diálogo lista e
 * o que vira `LiberacaoPresencialMotivoStruct` no request.
 */
export interface BloqueioDetectado {
    idVendaTipoBloqueio: number;
    titulo: string;
    descricao: string;
    /** O valor a autorizar — no desconto, o percentual; no crédito, o valor a receber sujeito a limite. Nulo onde não há valor. */
    valorLiberado: number | null;
}

/** O que a detecção precisa saber do documento, entregue pelo shell — as funções não olham o form inteiro. */
export interface ContextoDeBloqueio {
    linhas: FormGroup[];
    /** Teto de desconto do usuário, em %. Nulo = sem teto. */
    tetoDesconto: number | null;
    temDescontoDoDocumento: boolean;
    ehPedido: boolean;
    /** Pedido para cliente com título vencido, com o bloqueio ligado na configuração (§15 #7). */
    clienteBarradoPorAtraso: boolean;
    cliente: ClienteDocumentoStruct | null;
    totalDoDocumento: number;
    /**
     * Σ dos recebimentos cujas formas têm "Validar limite de crédito" (task 150887): é o que consome o
     * limite do cliente, e não o total do documento. Zero = nenhuma forma valida limite.
     */
    valorSujeitoALimite: number;
    /**
     * Itens já gravados que saíram do documento ("Descrição × qtde"), quando o usuário NÃO tem a
     * permissão "Remover item" (T03). Vazio = nada a liberar. Liberação gravada não vale aqui:
     * cada remoção é um fato novo.
     */
    itensGravadosRemovidos: string[];
    /** As liberações JÁ gravadas neste documento: o que foi liberado ontem não se pede de novo. */
    liberacoes: VendaLiberacao[];
}

/**
 * Detecta, ANTES de enviar, os bloqueios que o servidor recusaria (§15) e que a §6.5 permite
 * liberar na hora. A ordem é a da story: desconto, crédito, atraso.
 *
 * A conta do desconto é a MESMA da folha (`descontoEfetivoDoItem`, item a item, contra o teto
 * levantado pela maior liberação já concedida) — para o diálogo pedir exatamente o que o servidor
 * exigiria, e não um número diferente que ele recusaria de novo.
 *
 * O crédito vale no orçamento e no pedido (Pedro, 20/10: se barrar, não pode nem salvar); o atraso
 * só no PEDIDO: orçamento não cobra título (§15 #7).
 */
export function detectarBloqueios(ctx: ContextoDeBloqueio): BloqueioDetectado[] {
    const bloqueios: BloqueioDetectado[] = [];

    const desconto = bloqueioDeDesconto(ctx);

    if (desconto) bloqueios.push(desconto);

    const credito = bloqueioDeCredito(ctx);

    if (credito) bloqueios.push(credito);

    if (ctx.itensGravadosRemovidos.length > 0) bloqueios.push(bloqueioDeRemocao(ctx.itensGravadosRemovidos));

    if (ctx.ehPedido) {
        if (ctx.clienteBarradoPorAtraso && !jaLiberado(ctx, VendaTipoBloqueioEnum.TituloEmAtraso)) {
            bloqueios.push({
                idVendaTipoBloqueio: VendaTipoBloqueioEnum.TituloEmAtraso,
                titulo: 'Cliente com títulos vencidos',
                descricao: `O cliente tem ${reais(ctx.cliente?.valorVencido ?? 0)} em títulos vencidos e a empresa não permite pedido de venda nessa situação.`,
                valorLiberado: null,
            });
        }
    }

    return bloqueios;
}

/** Os bloqueios como o request os leva: só o tipo e o valor autorizado. */
export function motivosDaLiberacao(bloqueios: BloqueioDetectado[]): LiberacaoPresencialMotivoStruct[] {
    return bloqueios.map((b) => ({ idVendaTipoBloqueio: b.idVendaTipoBloqueio, valorLiberado: b.valorLiberado }));
}

function bloqueioDeDesconto(ctx: ContextoDeBloqueio): BloqueioDetectado | null {
    if (ctx.tetoDesconto == null) return null;

    // A liberação levanta o teto até o valor autorizado — e só até ele (§6.5.3).
    const liberados = ctx.liberacoes
        .filter((l) => l.idVendaTipoBloqueio === VendaTipoBloqueioEnum.Desconto && l.valorLiberado != null)
        .map((l) => Number(l.valorLiberado));
    const teto = Math.max(ctx.tetoDesconto, ...liberados);

    let pior: { sequencia: number; efetivo: number } | null = null;

    ctx.linhas.forEach((linha, indice) => {
        if (!linha.get('dados.idProduto')?.value) return;

        const efetivo = descontoEfetivoDoItem(linha);

        if (efetivo > teto && (pior == null || efetivo > pior.efetivo)) pior = { sequencia: indice + 1, efetivo };
    });

    if (pior == null) return null;

    const { sequencia, efetivo } = pior as { sequencia: number; efetivo: number };
    const origem = ctx.temDescontoDoDocumento ? ' (inclui o desconto do documento)' : '';

    return {
        idVendaTipoBloqueio: VendaTipoBloqueioEnum.Desconto,
        titulo: 'Desconto acima do limite',
        descricao: `O item ${sequencia} está com ${percentual(efetivo)} de desconto${origem} e o seu limite é de ${percentual(teto)}.`,
        // O MAIOR desconto do documento: liberado ele, todos os outros cabem.
        valorLiberado: efetivo,
    };
}

/**
 * Limite de crédito (task 150887): o que se compara com o limite disponível é a soma dos recebimentos
 * cujas formas validam limite, não o total do documento — dinheiro à vista não consome limite. O
 * orquestrador faz a mesma conta no salvar (ValidarLimiteDeCredito).
 */
function bloqueioDeCredito(ctx: ContextoDeBloqueio): BloqueioDetectado | null {
    const disponivel = ctx.cliente?.limiteDisponivel;
    const sujeito = ctx.valorSujeitoALimite;

    // Nulo = cliente sem limite a controlar (é diferente de zero). Sem recebimento que valide limite, nada a conferir.
    if (disponivel == null || sujeito <= 0 || sujeito <= disponivel) return null;

    const liberadoParaEsteValor = ctx.liberacoes.some((l) =>
        l.idVendaTipoBloqueio === VendaTipoBloqueioEnum.LimiteDeCredito && Number(l.valorLiberado ?? 0) >= sujeito);

    if (liberadoParaEsteValor) return null;

    return {
        idVendaTipoBloqueio: VendaTipoBloqueioEnum.LimiteDeCredito,
        titulo: 'Limite de crédito excedido',
        descricao: `Os recebimentos que consomem limite somam ${reais(sujeito)} e passam do limite disponível de ${reais(disponivel)} do cliente.`,
        valorLiberado: sujeito,
    };
}

/**
 * Remoção de item gravado (T03): o diálogo é aberto no clique de remover (vd-itens) e, de novo no
 * salvar, só se houver outro bloqueio junto. O servidor exige a permissão 223 de quem libera.
 */
export function bloqueioDeRemocao(removidos: string[]): BloqueioDetectado {
    return {
        idVendaTipoBloqueio: VendaTipoBloqueioEnum.RemocaoDeItem,
        titulo: 'Remoção de item gravado',
        descricao: `Remover: ${removidos.join('; ')}. Você não tem a permissão "Remover item".`,
        valorLiberado: null,
    };
}

/**
 * Conversão de orçamento vencido em pedido (§12.7): o diálogo é aberto no mover, no board e no campo Situação. Quem
 * decide que venceu é o servidor (`isVencido` do card e do detalhe, pelo fuso do usuário), não o relógio do navegador.
 */
export function bloqueioDeOrcamentoVencido(dataValidade: string): BloqueioDetectado {
    return {
        idVendaTipoBloqueio: VendaTipoBloqueioEnum.OrcamentoVencido,
        titulo: 'Orçamento vencido',
        descricao: `Orçamento vencido em ${new Date(dataValidade).toLocaleDateString('pt-BR')}. A conversão em pedido exige liberação.`,
        valorLiberado: null,
    };
}

function jaLiberado(ctx: ContextoDeBloqueio, tipo: VendaTipoBloqueioEnum): boolean {
    return ctx.liberacoes.some((l) => l.idVendaTipoBloqueio === tipo);
}

function percentual(valor: number): string {
    return `${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
}

function reais(valor: number): string {
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
